/**
 * Admin ROI Configuration Tab
 * Set/edit per-tool minutes saved, bot exclusion, audit log, CSV export.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { History, ExternalLink, Search, Edit3, X, Save, Bot, Info } from 'lucide-react';
import garageAPI from '../../../services/garage/api';
import ConfirmModal from '../modals/ConfirmModal';
import GarageLoadingScreen from './GarageLoadingScreen';
import { getAdminPersonaScopes, isDomainAdmin } from '../constants/roleAccess';
import './GarageRoiConfigTab.css';

const formatHrs = (mins) => {
  if (mins == null) return '—';
  return `${(mins / 60).toFixed(1)} hrs`;
};

const StatusPill = ({ status }) => {
  const map = {
    configured: { label: 'Configured', cls: 'roi-pill-configured' },
    unconfigured: { label: 'Unconfigured', cls: 'roi-pill-unconfigured' },
    exempt: { label: 'Exempt', cls: 'roi-pill-exempt' },
  };
  const cfg = map[status] || map.unconfigured;
  return <span className={`roi-status-pill ${cfg.cls}`}>{cfg.label}</span>;
};

const GarageRoiConfigTab = ({
  userRole,
  accessPersona = null,
  accessPersonas = [],
  initialToolId = null,
}) => {
  const domainAdmin = isDomainAdmin(userRole);
  const adminPersonaScopes = getAdminPersonaScopes(
    userRole,
    accessPersonas,
    accessPersona
  );
  const [tools, setTools] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingTool, setEditingTool] = useState(null);
  const [highlightedToolId, setHighlightedToolId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editNote, setEditNote] = useState('');
  const [recompute, setRecompute] = useState('recompute');
  const [saving, setSaving] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [auditEntries, setAuditEntries] = useState([]);
  const [toast, setToast] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    variant: 'default',
    onConfirm: null,
  });

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const closeConfirm = () => {
    setConfirmModal((prev) => ({ ...prev, isOpen: false, onConfirm: null }));
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Admin config: all envs so TEST/PRD tools remain configurable.
      // Backend auto-scopes the list for persona admins.
      const toolRows = await garageAPI.getRoiTools({ window: '90d', status: 'all' }, true);
      setTools(toolRows || []);
      // Global settings (bot exclusion) remain full-admin-only.
      if (!domainAdmin) {
        const roiSettings = await garageAPI.getRoiSettings();
        setSettings(roiSettings);
      }
    } catch (err) {
      console.error('Failed to load ROI config', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load ROI config');
    } finally {
      setLoading(false);
    }
  }, [domainAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!initialToolId || loading) return;
    const target = tools.find((tool) => String(tool.toolId) === String(initialToolId));
    if (!target) return;
    setStatusFilter('all');
    setSearch(target.name || '');
    setHighlightedToolId(String(initialToolId));
    const timer = window.setTimeout(() => {
      Array.from(document.querySelectorAll('[data-roi-tool-id]'))
        .find((row) => row.dataset.roiToolId === String(initialToolId))
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [initialToolId, loading, tools]);

  const statusCounts = useMemo(() => ({
    all: tools.length,
    configured: tools.filter((t) => t.roiStatus === 'configured').length,
    unconfigured: tools.filter((t) => t.roiStatus === 'unconfigured').length,
    exempt: tools.filter((t) => t.roiStatus === 'exempt').length,
  }), [tools]);

  const filtered = useMemo(() => {
    return tools.filter((t) => {
      if (statusFilter !== 'all' && t.roiStatus !== statusFilter) return false;
      if (search && !t.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [tools, statusFilter, search]);

  const openEdit = (tool) => {
    setEditingTool(tool);
    setEditValue(tool.minPerSession != null ? String(tool.minPerSession) : '');
    setEditNote('');
    setRecompute('recompute');
  };

  const handleSave = async () => {
    if (!editingTool) return;
    const val = editValue === '' ? null : Number(editValue);
    if (val !== null && (Number.isNaN(val) || val < 0)) {
      showToast('Enter a valid non-negative number');
      return;
    }
    setSaving(true);
    try {
      await garageAPI.updateToolRoi(editingTool.toolId, {
        minPerSession: val,
        historicalTreatment: editingTool.minPerSession != null ? recompute : 'recompute',
        note: editNote || null,
      });
      setEditingTool(null);
      showToast('ROI value saved');
      await load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const runExempt = async () => {
    if (!editingTool) return;
    setSaving(true);
    closeConfirm();
    try {
      await garageAPI.markToolRoiExempt(editingTool.toolId, editNote || null);
      setEditingTool(null);
      showToast('Tool marked exempt');
      await load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Exempt failed');
    } finally {
      setSaving(false);
    }
  };

  const runUnexempt = async () => {
    if (!editingTool) return;
    setSaving(true);
    closeConfirm();
    try {
      await garageAPI.unexemptToolRoi(editingTool.toolId, editNote || null);
      setEditingTool(null);
      showToast('Tool un-exempted — set minutes to include it in ROI');
      await load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Un-exempt failed');
    } finally {
      setSaving(false);
    }
  };

  const requestExempt = () => {
    if (!editingTool) return;
    setConfirmModal({
      isOpen: true,
      title: 'Mark tool as exempt?',
      message: `"${editingTool.name}" will be removed from Time Saved calculations. Minutes per session will be cleared. You can un-exempt it later from this same editor.`,
      confirmText: 'Mark exempt',
      variant: 'warning',
      onConfirm: runExempt,
    });
  };

  const requestUnexempt = () => {
    if (!editingTool) return;
    setConfirmModal({
      isOpen: true,
      title: 'Un-exempt this tool?',
      message: `"${editingTool.name}" will return to Unconfigured status. Set minutes per session afterward to include it in Time Saved / ROI.`,
      confirmText: 'Un-exempt',
      variant: 'default',
      onConfirm: runUnexempt,
    });
  };

  const toggleBotExclusion = async () => {
    if (!settings) return;
    try {
      const next = await garageAPI.updateRoiSettings({
        botExclusionEnabled: !settings.botExclusionEnabled,
      });
      setSettings(next);
      showToast(`Automation exclusion ${next.botExclusionEnabled ? 'ON' : 'OFF'}`);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to update setting');
    }
  };

  const openAudit = async () => {
    setShowAudit(true);
    try {
      const entries = await garageAPI.getRoiAuditLog({ limit: 50 });
      setAuditEntries(entries || []);
    } catch (err) {
      setAuditEntries([]);
      showToast('Failed to load audit log');
    }
  };

  const handleExport = async () => {
    try {
      const blob = await garageAPI.exportRoiCsv('90d', true);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ai_garage_roi_config.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showToast('CSV export failed');
    }
  };

  if (loading) {
    return <GarageLoadingScreen message="Loading ROI configuration…" />;
  }

  if (error) {
    return (
      <div className="roi-config-error">
        <p>{error}</p>
        <p className="roi-config-error-hint">
          If columns/tables are missing, run: <code>python -m scripts.migrate_roi_schema</code>
        </p>
        <button type="button" onClick={load}>Retry</button>
      </div>
    );
  }

  return (
    <div className="roi-config">
      <div className="roi-config-header">
        <div>
          <h2>ROI Configuration</h2>
          <p>Set time-saved-per-session values for each tool. Values feed the Tools Dashboard and user Time Saved cards. User avg shows calibration from reports (badge at N≥10).</p>
        </div>
        <div className="roi-config-header-actions">
          <button type="button" className="roi-btn-secondary" onClick={openAudit}>
            <History size={14} /> Audit log
          </button>
          <button type="button" className="roi-btn-secondary" onClick={handleExport}>
            <ExternalLink size={14} /> Export CSV
          </button>
        </div>
      </div>

      {domainAdmin && (
        <div style={{
          padding: '10px 16px',
          backgroundColor: '#e3f2fd',
          border: '1px solid #2196f3',
          borderRadius: '8px',
          marginBottom: '16px',
          fontSize: '13px',
          color: '#1565c0',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <Info size={16} />
          <span>
            <strong>{adminPersonaScopes.join(', ')} Admin View:</strong> Showing assigned-persona apps &amp; gems only. Audit log and CSV export are scoped to those tools.
          </span>
        </div>
      )}

      {!domainAdmin && (
      <div className="roi-global-settings">
        <div className="roi-global-label">Global settings</div>
        <div className="roi-global-grid">
          <div className="roi-global-tile">
            <div>
              <div className="roi-global-title">Automation user accounts exclusion</div>
              <div className="roi-global-desc">Exclude service accounts matching automated-*, test-*, *.bot</div>
            </div>
            <button
              type="button"
              className={`roi-toggle ${settings?.botExclusionEnabled ? 'on' : ''}`}
              onClick={toggleBotExclusion}
              aria-label="Toggle automation exclusion"
            >
              <span className="roi-toggle-knob" />
            </button>
          </div>
          <div className="roi-global-tile">
            <div>
              <div className="roi-global-title">New-tool default</div>
              <div className="roi-global-desc">5 min / session applied only for migrated defaults</div>
            </div>
            <span className="roi-mono-badge">5m</span>
          </div>
          <div className="roi-global-tile muted">
            <div>
              <div className="roi-global-title">Dollarization <span className="roi-v2">V2</span></div>
              <div className="roi-global-desc">Blended $/hr rate for ROI in dollars</div>
            </div>
            <span className="roi-mono-badge muted">Not enabled</span>
          </div>
        </div>
        <div className="roi-info-banner">
          <Info size={14} />
          <span>Persona overrides and dollarization are v2 features. Schema accepts them — configure later without a migration.</span>
        </div>
      </div>
      )}

      <div className="roi-filters">
        <div className="roi-search">
          <Search size={14} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tools..."
          />
        </div>
        <div className="roi-status-filters">
          {[
            { k: 'all', l: `All ${statusCounts.all}` },
            { k: 'configured', l: `Configured ${statusCounts.configured}` },
            { k: 'unconfigured', l: `Unconfigured ${statusCounts.unconfigured}` },
            { k: 'exempt', l: `Exempt ${statusCounts.exempt}` },
          ].map((f) => (
            <button
              key={f.k}
              type="button"
              className={statusFilter === f.k ? 'active' : ''}
              onClick={() => setStatusFilter(f.k)}
            >
              {f.l}
            </button>
          ))}
        </div>
      </div>

      <div className="roi-table-wrap">
        <table className="roi-table">
          <thead>
            <tr>
              <th>Tool</th>
              <th>Type</th>
              <th>Status</th>
              <th className="num">Min / Session</th>
              <th className="num">User avg</th>
              <th className="num">Sessions (90d)</th>
              <th className="num">Time Saved</th>
              <th>Last Edited</th>
              <th className="num">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((t) => (
              <tr
                key={t.toolId}
                data-roi-tool-id={t.toolId}
                className={String(t.toolId) === highlightedToolId ? 'roi-notification-target' : undefined}
              >
                <td>
                  <div className="roi-tool-name">
                    {t.name}
                    {t.defaulted && <span className="roi-defaulted">DEFAULTED</span>}
                  </div>
                  {t.makerEmail && <div className="roi-tool-maker">Maker: {t.makerEmail}</div>}
                </td>
                <td>
                  <span className={`roi-type-badge ${(t.type || '').toLowerCase()}`}>
                    {(t.type || '').toUpperCase()}
                  </span>
                </td>
                <td><StatusPill status={t.roiStatus} /></td>
                <td className="num mono">{t.minPerSession != null ? `${t.minPerSession}m` : '—'}</td>
                <td className="num">
                  {t.userValidatedAvg != null ? (
                    <div className="roi-user-avg">
                      <span className="mono">{t.userValidatedAvg}m</span>
                      <span className={`roi-div-badge ${t.divergenceBadge || 'pending'}`}>
                        {t.divergenceBadge
                          ? `${t.divergenceBadge} · n=${t.reportCount}`
                          : `n=${t.reportCount || 0}`}
                      </span>
                    </div>
                  ) : (
                    <span className="roi-muted">—</span>
                  )}
                </td>
                <td className="num sessions">{(t.sessions || 0).toLocaleString()}</td>
                <td className="num saved">{formatHrs(t.totalMinutesSaved)}</td>
                <td>
                  <div className="roi-edited-by">{t.lastEditedBy || '—'}</div>
                  <div className="roi-edited-at">{t.lastEditedAt ? t.lastEditedAt.slice(0, 10) : 'never'}</div>
                </td>
                <td className="num">
                  <button type="button" className="roi-edit-btn" onClick={() => openEdit(t)}>
                    <Edit3 size={12} /> Edit
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="roi-empty">No tools match your filters.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editingTool && (
        <div className="roi-modal-overlay" onClick={() => setEditingTool(null)}>
          <div className="roi-modal" onClick={(e) => e.stopPropagation()}>
            <div className="roi-modal-header">
              <div>
                <h3>Configure ROI</h3>
                <p>{editingTool.name}</p>
              </div>
              <button type="button" className="roi-icon-btn" onClick={() => setEditingTool(null)}>
                <X size={16} />
              </button>
            </div>
            <div className="roi-modal-body">
              <div className="roi-modal-tiles">
                <div>
                  <div className="label">Current value</div>
                  <div className="value">{editingTool.minPerSession != null ? `${editingTool.minPerSession} min` : '—'}</div>
                </div>
                <div>
                  <div className="label">Sessions (90d)</div>
                  <div className="value sessions">{(editingTool.sessions || 0).toLocaleString()}</div>
                </div>
              </div>
              <label className="roi-field-label">Minutes saved per session</label>
              <div className="roi-input-row">
                <input
                  type="number"
                  min={0}
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  placeholder="e.g. 15"
                />
                <span>min</span>
              </div>
              {editValue !== '' && !Number.isNaN(Number(editValue)) && (
                <div className="roi-projected">
                  Projected total: <strong>{formatHrs((editingTool.sessions || 0) * Number(editValue))}</strong>
                  {' '}({(editingTool.sessions || 0).toLocaleString()} × {editValue}m)
                </div>
              )}
              {editingTool.minPerSession != null && (
                <div className="roi-hist-block">
                  <div className="roi-field-label">Historical sessions</div>
                  <label className={`roi-radio-card ${recompute === 'lock' ? 'active' : ''}`}>
                    <input type="radio" checked={recompute === 'lock'} onChange={() => setRecompute('lock')} />
                    <div>
                      <strong>Lock at old rate</strong>
                      <span>Historical sessions keep {editingTool.minPerSession}m. New sessions use the new value. (v1 audits choice; rate history table deferred)</span>
                    </div>
                  </label>
                  <label className={`roi-radio-card ${recompute === 'recompute' ? 'active' : ''}`}>
                    <input type="radio" checked={recompute === 'recompute'} onChange={() => setRecompute('recompute')} />
                    <div>
                      <strong>Recompute all</strong>
                      <span>All 90d sessions use the new rate. Program totals shift immediately.</span>
                    </div>
                  </label>
                </div>
              )}
              <label className="roi-field-label">Change note</label>
              <input
                value={editNote}
                onChange={(e) => setEditNote(e.target.value)}
                placeholder="e.g. Validated with 5-user time study"
              />
            </div>
            <div className="roi-modal-footer">
              {editingTool.roiStatus === 'exempt' ? (
                <button type="button" className="roi-link-btn" onClick={requestUnexempt} disabled={saving}>
                  Un-exempt tool
                </button>
              ) : (
                <button type="button" className="roi-link-btn" onClick={requestExempt} disabled={saving}>
                  Mark tool as exempt from ROI
                </button>
              )}
              <div className="roi-modal-footer-right">
                <button type="button" className="roi-btn-secondary" onClick={() => setEditingTool(null)}>Cancel</button>
                <button type="button" className="roi-btn-primary" onClick={handleSave} disabled={saving}>
                  <Save size={14} /> {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        cancelText="Cancel"
        variant={confirmModal.variant}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirm}
      />

      {showAudit && (
        <div className="roi-modal-overlay" onClick={() => setShowAudit(false)}>
          <div className="roi-audit-panel" onClick={(e) => e.stopPropagation()}>
            <div className="roi-audit-header">
              <div>
                <h3><History size={16} /> Audit log</h3>
                <p>All ROI configuration changes</p>
              </div>
              <button type="button" className="roi-icon-btn" onClick={() => setShowAudit(false)}><X size={16} /></button>
            </div>
            <div className="roi-audit-body">
              {auditEntries.map((e) => (
                <div key={e.id} className="roi-audit-entry">
                  <div className="roi-audit-top">
                    <strong>{e.tool || e.settingKey || '—'}</strong>
                    <span>{e.at ? e.at.replace('T', ' ').slice(0, 16) : ''}</span>
                  </div>
                  <div>
                    <strong>{e.action}</strong>
                    {e.fromValue != null && <> · from <code>{e.fromValue}</code> to <code>{e.toValue}</code></>}
                    {e.fromValue == null && e.toValue != null && <> · set to <code>{e.toValue}</code></>}
                  </div>
                  <div className="roi-audit-actor">By <code>{e.actor}</code></div>
                  {e.note && <div className="roi-audit-note">&ldquo;{e.note}&rdquo;</div>}
                </div>
              ))}
              {auditEntries.length === 0 && <div className="roi-empty">No audit entries yet.</div>}
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="roi-toast">
          <Bot size={14} /> {toast}
        </div>
      )}
    </div>
  );
};

export default GarageRoiConfigTab;
